import { useState, useCallback } from "react";
import { signIn, completeNewPassword } from "../services/cognito";
import type { CognitoUser } from "amazon-cognito-identity-js";
import type { AuthStep } from "./useLogin";

export function useSignIn(
  setStep: (step: AuthStep) => void,
  setUserEmail: (email: string | null) => void,
  getCurrentSessionToken: () => Promise<string | null>,
  getCurrentUserEmail: () => Promise<string | null>
) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [pendingAttributes, setPendingAttributes] = useState<Record<string, string>>({});

  const handleLogin = useCallback(async () => {
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Ingresá tu usuario y contraseña");
      return;
    }

    setIsLoading(true);
    try {
      const result = await signIn(username.trim(), password);

      if (result.type === "NEW_PASSWORD_REQUIRED") {
        setPendingUser(result.cognitoUser!);
        setPendingAttributes(result.userAttributes || {});
        setStep("NEW_PASSWORD");
      } else {
        const token = await getCurrentSessionToken();
        if (!token) {
          throw new Error("No se pudo obtener el token de sesión.");
        }
        const email = await getCurrentUserEmail();
        setUserEmail(email);
        setStep("AUTHENTICATED");
      }
    } catch (err: any) {
      const message =
        err?.code === "NotAuthorizedException"
          ? "Usuario o contraseña incorrectos"
          : err?.code === "UserNotFoundException"
          ? "El usuario no existe"
          : err?.code === "UserNotConfirmedException"
          ? "Tu cuenta aún no fue confirmada"
          : err?.message || "Error al iniciar sesión";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [username, password, setStep, setUserEmail, getCurrentSessionToken, getCurrentUserEmail]);

  const handleNewPassword = useCallback(async () => {
    setError("");

    if (!newPassword.trim()) {
      setError("Ingresá tu nueva contraseña");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (newPassword.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (!pendingUser) {
      setError("Error inesperado. Intentá iniciar sesión nuevamente.");
      setStep("LOGIN");
      return;
    }

    setIsLoading(true);
    try {
      await completeNewPassword(pendingUser, newPassword, pendingAttributes);
      
      const token = await getCurrentSessionToken();
      if (!token) {
        throw new Error("No se pudo obtener el token de sesión.");
      }
      const email = await getCurrentUserEmail();
      setUserEmail(email);
      setStep("AUTHENTICATED");
    } catch (err: any) {
      const message =
        err?.code === "InvalidPasswordException"
          ? "La contraseña no cumple los requisitos de seguridad"
          : err?.message || "Error al cambiar la contraseña";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [newPassword, confirmPassword, pendingUser, pendingAttributes, setStep, setUserEmail, getCurrentSessionToken, getCurrentUserEmail]);

  return {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    setError,
    handleLogin,
    handleNewPassword,
    setPendingUser,
    setPendingAttributes
  };
}
